import React, { useState, useRef, useEffect } from 'react';
import styles from 'feature/common/landing/download/index.module.scss';
import { useTranslation } from 'react-i18next';
import autopass from 'images/landing/app_autopass.png';
import cathay from 'images/landing/app_cathay.png';
import cpcpay from 'images/landing/app_cpcpay.png';
import mytoyota from 'images/landing/app_mytoyota.png';
import iknow99 from 'images/landing/app_iknow99.png';
import waffleCity from 'images/landing/app_waffle.png';
import linebank from 'images/landing/app_linebank.png';
import npcpay from 'images/landing/app_npc.png';
import lexusPlus from 'images/landing/app_lexusplus.png';
import className from 'classnames/bind';
import downloadIcon from 'images/icon_download.svg';
import arrow from 'images/arrow_small.svg';
import browserIcon from 'images/icon_browser.svg';
import { Button } from 'mochi-react-component';
import amplitudeService from 'service/amplitude';
import ReactPixel from 'react-facebook-pixel';
import useIntersect from 'utils/intersect-helper';
import Carousel from 'components/carousel';
import {
  autopassDownloadLink,
  cpcpayDownloadLink,
  waffleDownloadLink,
  iknow99DownloadLink,
  mochiClientWebUrl,
  linebankDownloadLink,
  myToyotaDownloadLink,
  npcpayDownloadLink,
  lexusPlusDownloadLink,
} from 'config';
import { useResize } from 'utils/resize-helper';
import { deviceOSType } from 'utils/device-util';

const cx = className.bind(styles);

const Download = () => {
  const device = useResize();
  const isMobile = device === 'mobile';
  const { t } = useTranslation();
  const downloadRef = useRef();
  const [contentshow, setContentShow] = useState(false);
  const [swiped, setSwiped] = useState(false);

  const [setElement] = useIntersect(() => {
    setTimeout(() => {
      setContentShow(true);
    }, 0);
    ReactPixel.track('ViewContent', {
      content_name: 'download',
    });
  });

  useEffect(() => {
    setElement([downloadRef.current]);
  }, [setElement]);

  const contentClass = cx({
    content: true,
    show: contentshow,
  });

  const clientsGroup = [
    {
      name: '車麻吉',
      id: 'autopass',
      title: t('landing.download.autopass'),
      image: autopass,
      link: deviceOSType(autopassDownloadLink.ios, autopassDownloadLink.android, autopassDownloadLink.desktop),
    },
    {
      name: 'LINE Bank',
      id: 'linebank',
      title: t('landing.download.lineBank'),
      image: linebank,
      link: deviceOSType(linebankDownloadLink.ios, linebankDownloadLink.android, linebankDownloadLink.desktop),
    },
    {
      name: '中油 pay',
      id: 'cpcpay',
      title: t('landing.download.cpcpay'),
      image: cpcpay,
      link: deviceOSType(cpcpayDownloadLink.ios, cpcpayDownloadLink.android, cpcpayDownloadLink.desktop),
    },
    {
      name: '全國加油站',
      id: 'npcpay',
      title: t('landing.download.npcpay'),
      image: npcpay,
      link: deviceOSType(npcpayDownloadLink.ios, npcpayDownloadLink.android, npcpayDownloadLink.desktop),
    },
    {
      name: 'MyToyota',
      id: 'mytoyota',
      title: t('landing.download.mytoyota'),
      image: mytoyota,
      link: deviceOSType(myToyotaDownloadLink.ios, myToyotaDownloadLink.android, myToyotaDownloadLink.desktop),
    },
    {
      name: 'LEXUS Plus',
      id: 'lexusPlus',
      title: t('landing.download.lexus_plus'),
      image: lexusPlus,
      link: deviceOSType(lexusPlusDownloadLink.ios, lexusPlusDownloadLink.android, lexusPlusDownloadLink.desktop),
    },
    {
      name: '愛車99',
      id: 'iknow99',
      title: t('landing.download.iknow99'),
      image: iknow99,
      link: deviceOSType(iknow99DownloadLink.ios, iknow99DownloadLink.android, iknow99DownloadLink.desktop),
    },
    {
      name: '窩福社區',
      id: 'waffle',
      title: t('landing.download.waffleCity'),
      image: waffleCity,
      link: deviceOSType(waffleDownloadLink.ios, waffleDownloadLink.android, waffleDownloadLink.desktop),
    },
    {
      name: '國泰優惠',
      id: 'cathayMr',
      title: t('landing.download.cathay_my_reward'),
      image: cathay,
      link: mochiClientWebUrl(),
    },
  ];

  const Content = () => {
    const Card = ({ client }) => {
      const isCathayMr = client.id === 'cathayMr';

      return (
        <a className={styles.block} href={client.link}>
          <img className={styles.image} src={client.image} alt={client.name} loading="lazy" />
          <div className={styles.tag}>{client.id === 'cathayMr' ? 'WEB' : 'APP'}</div>
          <div className={styles.title}>{client.title}</div>
          <Button
            type="outline"
            theme="blue"
            size="with-icon"
            iconLeft={isCathayMr ? browserIcon : downloadIcon}
            onClick={() => {
              amplitudeService.track('服務介紹頁-立即下載_點擊', {
                app: client.name,
              });
            }}
          >
            {isCathayMr ? t('landing.download.use_text') : t('landing.download.download_text')}
          </Button>
        </a>
      );
    };

    if (isMobile) {
      return (
        <Carousel
          onSwipe={() => setSwiped(true)}
          loading={false}
          items={clientsGroup.map(client => {
            return <Card client={client} />;
          })}
          fullWidth={true}
          customWidth={250}
        />
      );
    }

    return clientsGroup.map(client => {
      return <Card client={client} />;
    });
  };

  const mobileHintClass = cx({
    'mobile-hint': true,
    hide: swiped,
  });

  return (
    <section id="Download" className={styles.download} ref={downloadRef}>
      <h2>{t('landing.download.title')}</h2>
      <h3>{t('landing.download.message')}</h3>
      <div className={contentClass}>{Content()}</div>
      <div className={mobileHintClass}>
        向右滑動
        <img src={arrow} alt="arrow" />
      </div>
    </section>
  );
};

export default Download;
